import SessionManager from "@Core/session";
import { IActivityDetails } from "@Models/IActivityDetails";
import { IAssessmentInstanceDetails } from "@Models/IAssessmentDetails";
import { IFeedbackTemplateDetails } from "@Models/IFeedbackTemplateDetails";
import { ITaskSubmissionDetails } from "@Models/ITaskDetails";
import { useState } from "react";
import { IExtendedAssessmentInstanceDetails, IUseAssessmentData } from "../types";
import { extendAssessmentInstanceDetails } from "./../helpers";
import { useUserActivitiesInstancesMap, useTemplatesMap, useUsersMap } from "./../queries";

interface IUseStudentSubmissionsData extends IUseAssessmentData<IAssessmentInstanceDetails, ITaskSubmissionDetails> {
  data: {
    activityInstanceId: {
      isLoading: boolean;
      onSelect: (activityInstanceId: ITaskSubmissionDetails["activityInstanceId"]) => void;
      videoActivity: IActivityDetails;
    };
  };
  submission: IExtendedAssessmentInstanceDetails;
  template: IFeedbackTemplateDetails;
}

interface IProps {
  assessmentInstance: IAssessmentInstanceDetails;
}

const useStudentSubmissions = ({ assessmentInstance }: IProps): IUseStudentSubmissionsData => {
  const { userId } = SessionManager.getServiceUser();
  const [videoActivity, setVideoActivity] = useState<IActivityDetails>();

  const { data: templatesMap, isLoading, isFetched: isTemplatesMapFetched } = useTemplatesMap();
  const template = templatesMap.get(assessmentInstance?.feedbackTemplateId);

  const { data: usersMap, isFetched: areUsersFetched } = useUsersMap();

  const {
    data: activitiesInstancesMap,
    isFetched: isActivitiesInstancesMapFetched,
    isFetching: isActivitiesInstancesMapFetching,
    isLoading: isActivitiesInstancesMapLoading,
  } = useUserActivitiesInstancesMap(userId, assessmentInstance);

  const handleActivityInstanceSelect = (activityInstanceId: ITaskSubmissionDetails["activityInstanceId"]) =>
    setVideoActivity(activitiesInstancesMap.get(activityInstanceId));

  const isEverythingFetched = isTemplatesMapFetched && areUsersFetched && isActivitiesInstancesMapFetched;

  const submission = isEverythingFetched
    ? extendAssessmentInstanceDetails(
        assessmentInstance,
        usersMap.get(assessmentInstance.owner)?.fullName,
        activitiesInstancesMap
      )
    : null;

  return {
    data: {
      activityInstanceId: {
        isLoading:
          !isEverythingFetched ||
          isActivitiesInstancesMapLoading ||
          !submission?.owner ||
          isActivitiesInstancesMapFetching,
        onSelect: handleActivityInstanceSelect,
        videoActivity,
      },
    },
    isLoading: !isEverythingFetched || isLoading || !submission?.assessmentId,
    submission,
    template,
  };
};

export default useStudentSubmissions;
